import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Clock, CheckCircle, XCircle, LogOut, Plus, 
  Search, Filter, ChevronDown, Loader2, Trash2, Edit3, 
  Save, X, Mail, Phone, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // New client form
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });

  // Edit notes
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  const token = localStorage.getItem('admin_token');

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin-portal');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/admin-portal');
      return;
    }
    fetchClients();
  }, [token, navigate, fetchClients]);

  const updateClientStatus = async (clientId, status) => {
    setIsSaving(true);
    try {
      await axios.patch(
        `${API}/clients/${clientId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchClients();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateClientNotes = async (clientId) => {
    setIsSaving(true);
    try {
      await axios.patch(
        `${API}/clients/${clientId}`,
        { notes: notesValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingNotes(null);
      fetchClients();
    } catch (err) {
      console.error('Failed to update notes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClient(null);
      fetchClients();
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  const addClient = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await axios.post(`${API}/clients/add`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAddModalOpen(false);
      setNewClient({ name: '', email: '', phone: '', service_type: '', message: '' });
      fetchClients();
    } catch (err) {
      console.error('Failed to add client:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin-portal');
  };

  const filteredClients = clients.filter(client => {
    const matchesFilter = activeFilter === 'all' || client.status === activeFilter;
    const matchesSearch = 
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: clients.length,
    pending: clients.filter(c => c.status === 'pending').length,
    approved: clients.filter(c => c.status === 'approved').length,
    rejected: clients.filter(c => c.status === 'rejected').length
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={40} className="text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-heading text-2xl font-medium text-gradient-gold">
              REAGANS
            </span>
            <span className="font-mono text-xs uppercase tracking-widest text-white/40 hidden sm:block">
              Admin Dashboard
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Cards - Fixed mobile grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {[
            { label: 'Total', value: stats.total, icon: Users, color: 'text-white' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-yellow-400' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-green-400' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-400' }
          ].map((stat, index) => (
            <div 
              key={index}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <stat.icon size={18} className={`${stat.color} flex-shrink-0`} />
                <span className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-white/40 truncate">
                  {stat.label}
                </span>
              </div>
              <p className="font-heading text-2xl sm:text-3xl text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col gap-3 sm:gap-4 mb-6">
          {/* Search */}
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="admin-search"
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-gold/50 transition-colors text-sm"
            />
          </div>

          {/* Filters + Add Button Row */}
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                data-testid={`filter-${filter}`}
                className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-mono text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest transition-all flex-shrink-0 ${
                  activeFilter === filter
                    ? 'bg-gold/20 text-gold border border-gold/30'
                    : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                }`}
              >
                {filter}
              </button>
            ))}
            
            {/* Add Button */}
            <button
              onClick={() => setIsAddModalOpen(true)}
              data-testid="admin-add-client"
              className="glass-button px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-mono text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest text-gold flex items-center gap-1 sm:gap-2 ml-auto"
            >
              <Plus size={14} />
              <span className="hidden xs:inline">Add</span>
            </button>
          </div>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {filteredClients.map((client) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`client-card-${client.id}`}
              className={`glass-card rounded-2xl p-6 cursor-pointer transition-all hover:border-gold/30 ${
                selectedClient?.id === client.id ? 'border-gold/50' : ''
              }`}
              onClick={() => setSelectedClient(client)}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-medium text-lg">{client.name}</h3>
                  <p className="text-white/50 text-sm">{client.email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase ${
                  client.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' :
                  client.status === 'approved' ? 'bg-green-400/10 text-green-400' :
                  'bg-red-400/10 text-red-400'
                }`}>
                  {client.status}
                </span>
              </div>

              {client.service_type && (
                <p className="text-white/40 text-sm mb-2">
                  <span className="text-white/60">Service:</span> {client.service_type}
                </p>
              )}

              <p className="text-white/30 text-xs">
                {formatDate(client.created_at)}
              </p>
            </motion.div>
          ))}

          {filteredClients.length === 0 && (
            <div className="col-span-2 text-center py-12 text-white/30">
              No clients found
            </div>
          )}
        </div>
      </main>

      {/* Client Detail Modal */}
      {selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedClient(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="client-detail-modal"
            className="relative z-10 w-full max-w-2xl glass-card rounded-3xl p-8 max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={() => setSelectedClient(null)}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <div className="mb-6">
              <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase ${
                selectedClient.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' :
                selectedClient.status === 'approved' ? 'bg-green-400/10 text-green-400' :
                'bg-red-400/10 text-red-400'
              }`}>
                {selectedClient.status}
              </span>
            </div>

            <h2 className="font-heading text-2xl text-white mb-2">{selectedClient.name}</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-white/60">
                <Mail size={16} className="text-gold/50" />
                <a href={`mailto:${selectedClient.email}`} className="hover:text-gold transition-colors">
                  {selectedClient.email}
                </a>
              </div>
              {selectedClient.phone && (
                <div className="flex items-center gap-3 text-white/60">
                  <Phone size={16} className="text-gold/50" />
                  <a href={`tel:${selectedClient.phone}`} className="hover:text-gold transition-colors">
                    {selectedClient.phone}
                  </a>
                </div>
              )}
              {selectedClient.service_type && (
                <div className="flex items-center gap-3 text-white/60">
                  <MessageSquare size={16} className="text-gold/50" />
                  {selectedClient.service_type}
                </div>
              )}
            </div>

            {selectedClient.message && (
              <div className="mb-6">
                <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-2">Message</h4>
                <p className="text-white/70 bg-white/5 rounded-xl p-4">{selectedClient.message}</p>
              </div>
            )}

            {/* Notes Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-mono text-xs uppercase tracking-widest text-white/40">Admin Notes</h4>
                {editingNotes !== selectedClient.id && (
                  <button
                    onClick={() => {
                      setEditingNotes(selectedClient.id);
                      setNotesValue(selectedClient.notes || '');
                    }}
                    className="text-gold/60 hover:text-gold transition-colors"
                  >
                    <Edit3 size={14} />
                  </button>
                )}
              </div>
              {editingNotes === selectedClient.id ? (
                <div className="space-y-2">
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white resize-none focus:outline-none focus:border-gold/50"
                    rows={3}
                    placeholder="Add notes..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateClientNotes(selectedClient.id)}
                      disabled={isSaving}
                      className="px-4 py-2 bg-gold/20 text-gold rounded-lg text-sm flex items-center gap-2"
                    >
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      Save
                    </button>
                    <button
                      onClick={() => setEditingNotes(null)}
                      className="px-4 py-2 bg-white/5 text-white/50 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-white/50 bg-white/5 rounded-xl p-4">
                  {selectedClient.notes || 'No notes yet'}
                </p>
              )}
            </div>

            {/* Timestamps */}
            <div className="text-white/30 text-xs space-y-1 mb-6">
              <p>Created: {formatDate(selectedClient.created_at)}</p>
              <p>Updated: {formatDate(selectedClient.updated_at)}</p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {selectedClient.status !== 'approved' && (
                <button
                  onClick={() => updateClientStatus(selectedClient.id, 'approved')}
                  disabled={isSaving}
                  data-testid="approve-client"
                  className="px-6 py-3 bg-green-400/10 text-green-400 rounded-xl font-mono text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-green-400/20 transition-colors"
                >
                  <CheckCircle size={16} />
                  Approve
                </button>
              )}
              {selectedClient.status !== 'rejected' && (
                <button
                  onClick={() => updateClientStatus(selectedClient.id, 'rejected')}
                  disabled={isSaving}
                  data-testid="reject-client"
                  className="px-6 py-3 bg-red-400/10 text-red-400 rounded-xl font-mono text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-red-400/20 transition-colors"
                >
                  <XCircle size={16} />
                  Reject
                </button>
              )}
              {selectedClient.status !== 'pending' && (
                <button
                  onClick={() => updateClientStatus(selectedClient.id, 'pending')}
                  disabled={isSaving}
                  className="px-6 py-3 bg-yellow-400/10 text-yellow-400 rounded-xl font-mono text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-yellow-400/20 transition-colors"
                >
                  <Clock size={16} />
                  Set Pending
                </button>
              )}
              <button
                onClick={() => deleteClient(selectedClient.id)}
                data-testid="delete-client"
                className="px-6 py-3 bg-white/5 text-white/50 rounded-xl font-mono text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-red-400/10 hover:text-red-400 transition-colors"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Client Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsAddModalOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="add-client-modal"
            className="relative z-10 w-full max-w-lg glass-card rounded-3xl p-8"
          >
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <h2 className="font-heading text-2xl text-white mb-6">Add New Client</h2>

            <form onSubmit={addClient} className="space-y-4">
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={newClient.name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                  Service Type
                </label>
                <input
                  type="text"
                  value={newClient.service_type}
                  onChange={(e) => setNewClient(prev => ({ ...prev, service_type: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
                  Notes
                </label>
                <textarea
                  value={newClient.message}
                  onChange={(e) => setNewClient(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white resize-none focus:outline-none focus:border-gold/50"
                />
              </div>
              <button
                type="submit"
                disabled={isSaving}
                className="w-full glass-button px-6 py-4 rounded-xl font-mono text-sm uppercase tracking-widest text-gold flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Client
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
